import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  FormControl,
} from "@mui/material";
import CustomizedTables from "../../components/common/GeneralTable";
import ProgressBarHorizontal from "../../components/common/ProgressBar";
import { getAllCertificates } from "../../components/services/companyAPI";
import LoadingComponent from "../../widgets/LoadingComponent";

const categoryColors = {
  normal: "#A5D6A7",
  proxVencer: "#FFF59D",
  criticos: "#FFCC80",
  vencidos: "#EF9A9A",
};

const Certificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCompanyState, setFilterCompanyState] = useState("userEnabled");

  // 🔎 Categorización
  const categorized = useMemo(() => {
    const filtered = certificates.filter((cert) => {
      if (filterCompanyState === "userEnabled") return cert.activeCompany;
      if (filterCompanyState === "userDisabled")
        return cert.activeCompany === false;
      return true; // all
    });

    return {
      normal: filtered.filter((c) => c.expirationDay > 60),
      proxVencer: filtered.filter(
        (c) => c.expirationDay > 30 && c.expirationDay <= 60
      ),
      criticos: filtered.filter(
        (c) => c.expirationDay > 0 && c.expirationDay <= 30
      ),
      vencidos: filtered.filter((c) => c.expirationDay <= 0),
      base: filtered,
    };
  }, [certificates, filterCompanyState]);

  const calculatePercent = (part, total) =>
    total > 0 ? ((part / total) * 100).toFixed(2) + "%" : "0%";

  const percentages = useMemo(() => {
    const totalSinVencidos = categorized.base.filter(
      (c) => c.expirationDay > 0
    ).length;

    return [
      {
        name: "Certificados al día (más de 60 días)",
        idKey: "normal",
        cantidad: categorized.normal.length,
        porcentaje: calculatePercent(
          categorized.normal.length,
          totalSinVencidos
        ),
        color: categoryColors.normal,
      },
      {
        name: "Certificados próximos a vencer (31 - 60 días)",
        idKey: "proxVencer",
        cantidad: categorized.proxVencer.length,
        porcentaje: calculatePercent(
          categorized.proxVencer.length,
          totalSinVencidos
        ),
        color: categoryColors.proxVencer,
      },
      {
        name: "Certificados urgentes por renovación (1 - 30 días)",
        idKey: "criticos",
        cantidad: categorized.criticos.length,
        porcentaje: calculatePercent(
          categorized.criticos.length,
          totalSinVencidos
        ),
        color: categoryColors.criticos,
      },
    ];
  }, [categorized]);

  // 🧠 Fetch inicial
  const getCertificates = async () => {
    setLoading(true);
    const result = await getAllCertificates();
    const certs = result?.certificates || [];
    setCertificates(certs);
    setLoading(false);
  };

  const handleChange = (e) => {
    setFilterCompanyState(e.target.value);
  };

  const filterByCategory = (category) => () => {
    const key = category.idKey || category;
    setFilteredCertificates(categorized[key]);
  };

  const backgroundRowColor = (row) => {
    if (row.expirationDay <= 0) return categoryColors.vencidos;
    if (row.expirationDay <= 30) return categoryColors.criticos;
    if (row.expirationDay <= 60) return categoryColors.proxVencer;
    return "#a5efa89c";
  };

  useEffect(() => {
    getCertificates();
  }, []);

  useEffect(() => {
    // Cada vez que se actualiza el filtro, se muestran todos los datos por defecto
    setFilteredCertificates(categorized.base);
  }, [categorized]);

  const tableColumns = [
    { name: "NIT", key: "identificationNumber" },
    { name: "RAZONES SOCIALES", key: "name" },
    { name: "EMAIL NOTIFICACION", key: "email" },
    { name: "FECHA DE VENCIMIENTO", key: "expirationDate" },
    { name: "DÍAS FALTANTES", key: "expirationDay" },
    { name: "COMPAÑÍA ACTIVA", key: "activeCompany" },
  ];

  return (
    <Container maxWidth>
      <LoadingComponent Loading={loading} />
      <Grid margin="2rem 1rem">
        <Typography variant="h3" textAlign="center" margin="0rem" color="#000">
          Certificados Digitales
        </Typography>
      </Grid>

      <ProgressBarHorizontal
        valuesPercentages={percentages}
        onClickChip={filterByCategory}
      />

      <FormControl fullWidth size="small">
        <InputLabel id="estado-usuario-label">Estado de compañía</InputLabel>
        <Grid>
          <Select
            labelId="estado-usuario-label"
            id="estado-usuario"
            size="small"
            value={filterCompanyState}
            onChange={handleChange}
            label="Estado de compañía"
          >
            <MenuItem value="userEnabled">ACTIVOS</MenuItem>
            <MenuItem value="userDisabled">INACTIVOS</MenuItem>
            <MenuItem value="all">TODOS</MenuItem>
          </Select>
        </Grid>
      </FormControl>

      <CustomizedTables
        rowsTitle={tableColumns}
        rowsValue={filteredCertificates}
        totalRows={categorized.base.length}
        rowDateName="expirationDate"
        filterTextField={() => {}}
        backgroundRowColor={backgroundRowColor}
        rowBoolean="activeCompany"
        rowLink="identificationNumber"
      />
    </Container>
  );
};

export default Certificates;
